// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || '/api',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3
}

// Purchase Order Status
export const PO_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

export const PO_STATUS_OPTIONS = [
  { value: PO_STATUS.ACTIVE, label: 'Active' },
  { value: PO_STATUS.INACTIVE, label: 'Inactive' }
]

// Common Roles
export const COMMON_ROLES = [
  'Developer',
  'Designer',
  'Project Manager',
  'QA Engineer',
  'DevOps Engineer',
  'Business Analyst',
  'Consultant',
  'Technical Lead',
  'Architect',
  'Support Engineer'
]

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMIT_OPTIONS: [10, 25, 50, 100],
  MAX_LIMIT: 100
}

// Sort Options
export const SORT_OPTIONS = {
  PO: [
    { value: 'po_number', label: 'PO Number' },
    { value: 'client_name', label: 'Client Name' },
    { value: 'total_amount', label: 'Total Amount' },
    { value: 'remaining_amount', label: 'Remaining Amount' },
    { value: 'issued_date', label: 'Issued Date' },
    { value: 'createdAt', label: 'Created Date' }
  ],
  USAGE_LOGS: [
    { value: 'usage_date', label: 'Usage Date' },
    { value: 'amount_used', label: 'Amount Used' },
    { value: 'createdAt', label: 'Created Date' }
  ]
}

export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc'
}

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  DATETIME: 'MMM dd, yyyy HH:mm',
  SHORT: 'MM/dd/yyyy',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
}

// Currency
export const CURRENCY = {
  DEFAULT: 'USD',
  SYMBOL: '$',
  LOCALE: 'en-US'
}

// Multi-Currency Support
export const CURRENCIES = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    position: 'before' // symbol position: 'before' or 'after'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE',
    position: 'after'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    position: 'before'
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    locale: 'ja-JP',
    position: 'before'
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    locale: 'en-CA',
    position: 'before'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    locale: 'en-AU',
    position: 'before'
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    locale: 'de-CH',
    position: 'before'
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Chinese Yuan',
    locale: 'zh-CN',
    position: 'before'
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    locale: 'en-IN',
    position: 'before'
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real',
    locale: 'pt-BR',
    position: 'before'
  }
}

export const CURRENCY_OPTIONS = Object.values(CURRENCIES).map(currency => ({
  value: currency.code,
  label: `${currency.name} (${currency.symbol})`,
  symbol: currency.symbol,
  code: currency.code
}))

// Validation Limits
export const VALIDATION_LIMITS = {
  PO_NUMBER: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 50
  },
  CLIENT_NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 100
  },
  ROLE_MAPPED: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50
  },
  DESCRIPTION: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 500
  },
  AMOUNT: {
    MIN: 0.01,
    MAX: 999999999.99
  }
}

// Toast Configuration
export const TOAST_CONFIG = {
  DURATION: 4000,
  POSITION: 'top-right',
  SUCCESS_DURATION: 3000,
  ERROR_DURATION: 5000
}

// Query Keys for React Query
export const QUERY_KEYS = {
  PURCHASE_ORDERS: 'purchaseOrders',
  PURCHASE_ORDER: 'purchaseOrder',
  USAGE_LOGS: 'usageLogs',
  USAGE_LOG: 'usageLog',
  DASHBOARD_STATS: 'dashboardStats',
  DASHBOARD_OVERVIEW: 'dashboardOverview',
  DASHBOARD_ALERTS: 'dashboardAlerts'
}

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed'
}

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  NOT_FOUND: 'The requested resource was not found.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  TIMEOUT: 'Request timed out. Please try again.',
  UNKNOWN: 'An unexpected error occurred.'
}

// Success Messages
export const SUCCESS_MESSAGES = {
  PO_CREATED: 'Purchase Order created successfully',
  PO_UPDATED: 'Purchase Order updated successfully',
  PO_DELETED: 'Purchase Order deleted successfully',
  USAGE_ADDED: 'Usage added successfully',
  USAGE_DELETED: 'Usage log deleted successfully'
}

// Chart Colors
export const CHART_COLORS = {
  PRIMARY: '#3B82F6',
  SUCCESS: '#10B981',
  WARNING: '#F59E0B',
  DANGER: '#EF4444',
  INFO: '#06B6D4',
  SECONDARY: '#6B7280',
  PALETTE: [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
    '#06B6D4', '#8B5CF6', '#F97316', '#84CC16'
  ]
}

// Dashboard Refresh Intervals (in milliseconds)
export const REFRESH_INTERVALS = {
  DASHBOARD: 30000, // 30 seconds
  REAL_TIME: 5000,  // 5 seconds
  SLOW: 60000       // 1 minute
}

// File Upload
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf']
}

// Responsive Breakpoints (matching Tailwind CSS)
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536
}

// Navigation Items
export const NAVIGATION = [
  {
    name: 'Dashboard',
    href: '/',
    icon: 'BarChart3'
  },
  {
    name: 'Purchase Orders',
    href: '/purchase-orders',
    icon: 'FileText'
  },
  {
    name: 'Create PO',
    href: '/purchase-orders/create',
    icon: 'Plus'
  },
  {
    name: 'Usage Logs',
    href: '/usage-logs',
    icon: 'Activity'
  }
]
